/**
 * RevenueStoredEconomicsRow — the ORDER-side money row, read from stored economics (card 2256).
 *
 * Presentational and pure: it renders the `storedEconomics` object of
 * `GET /admin/revenue-calculation/summary` and derives NOTHING. Every figure is the API's
 * metric object `{ value, basis, source, dataQuality, blockedBy }` in integer cents, so a
 * figure with no stored value still arrives `value: null` and leaves here as `— / Missing`.
 *
 * 🔑 WHY THIS EXISTS BESIDE `RevenueKpiRow` RATHER THAN REPLACING IT.
 *   The two rows measure DIFFERENT POPULATIONS and must never be read as one number:
 *     · this row  — the CANONICAL reporting population (all statuses, reporting exclusion
 *       applied on both the order and its owning user). Same rule AND same arithmetic as
 *       `GET /admin/analytics/executive`, so the two agree to the cent by construction.
 *     · RevenueKpiRow — the catalog/stored-column population (status-filtered, no reporting
 *       exclusion), which is a different set of orders.
 *   Card 2250 measured the gap at 248 vs 202 orders. Presenting them as one figure — or
 *   quietly widening one rule until they matched — is the exact dishonesty this page refuses.
 *   Both are therefore rendered, each labelled with its own predicate.
 *
 * ⛔ Reserve and provider fees are NOT blocked here. They were hard-coded `Q3`/`Q5` blockers
 *   until this card; both are ratified in DECISIONS-2026-08-25 and resolved per order at
 *   write time, so they are read off the stored row. A figure that genuinely has no stored
 *   value still refuses to resolve — never 0, because a zero cost prints a 100% margin.
 */

import { formatMetricValue, describeMetricBasis, isBlockedMetric } from '../../utils/revenueMetrics';
import styles from '../../pages/admin/RevenueCalculationPage.module.css';

/** The six stored figures in render order. Pure data — the row maps this list. */
export const STORED_DEFS = Object.freeze([
  { key: 'grossRevenue', label: 'Gross revenue', kind: 'money' },
  { key: 'reserveWithheld', label: 'Reserve withheld', kind: 'money' },
  { key: 'netSettledRevenue', label: 'Net settled revenue', kind: 'money' },
  { key: 'totalExpense', label: 'Total expense', kind: 'money' },
  { key: 'profit', label: 'Profit', kind: 'money' },
  { key: 'profitMargin', label: 'Profit margin', kind: 'percent' }
]);

/**
 * Pure. One stored figure, carrying its OWN `verified` / `estimated` badge.
 * A row resting on an unratified mapping is `estimated` and says so in the chip and the
 * tooltip — it is never silently promoted to `verified`.
 */
const StoredTile = ({ def, metric }) => {
  const blocked = isBlockedMetric(metric);
  const quality = metric?.dataQuality ?? 'missing';
  return (
    <div
      className={styles.kpiTile}
      data-testid={`stored-${def.key}`}
      data-blocked={blocked ? 'true' : 'false'}
      data-quality={quality}
      title={describeMetricBasis(metric)}
    >
      <p className={styles.kpiLabel}>{def.label}</p>
      <p className={blocked ? styles.kpiValueBlocked : styles.kpiValue}>
        {formatMetricValue(metric, def.kind)}
      </p>
      <span className={quality === 'verified' ? styles.chipOk : styles.chipBlocked}>
        {blocked ? `Blocked — ${metric?.blockedBy ?? 'no stored value'}` : quality}
      </span>
    </div>
  );
};

/**
 * PURE. Card 3656 — the version text for the stored row set, taken from the versions the API
 * OBSERVED on the rows it summed, ⛔ never from the engine constant. A mixed set says so:
 * publishing "version 2" over 206 v1 rows is exactly the defect this replaced.
 */
export const formatStoredVersion = (stored) => {
  const observed = stored?.observedEconomicsVersions
    ?? (Number.isInteger(stored?.economicsVersion) ? [stored.economicsVersion] : []);
  if (observed.length === 0) return 'none stored';
  if (observed.length === 1) return String(observed[0]);
  return `${observed.join(' + ')} (mixed)`;
};

/**
 * The stored-economics row. `stored` is the summary payload's `storedEconomics` object.
 *
 * `ordersWithoutEconomics` is REPORTED rather than folded in as zero: an order in the
 * population with no stored row understates every figure above it, and a reader has to be
 * able to see that hole rather than infer it.
 */
const RevenueStoredEconomicsRow = ({ stored }) => {
  if (!stored) return null;

  const population = stored.population ?? {};
  const withoutEconomics = Number(stored.ordersWithoutEconomics ?? 0);
  const quality = stored.dataQuality ?? {};

  return (
    <div data-testid="stored-economics-content">
      <p className={styles.sectionLabel} data-testid="stored-economics-label">
        Stored per-order economics · version {formatStoredVersion(stored)}
      </p>
      <p className={styles.sectionNote} data-testid="stored-economics-basis">
        Read from the <code>order_economics</code> table at the version each row was STORED at —
        resolved and stored when each order was placed, so a price edited today cannot move a
        figure resolved months ago. ⛔ This is NOT the catalog matrix below.
        {population.matches ? ` Matches ${population.matches} to the cent.` : ''}
      </p>
      <p className={styles.scopeLine} data-testid="stored-economics-population">
        {stored.orderCount} of {population.orderCount ?? stored.orderCount} orders in the{' '}
        {population.basis ?? 'reporting population'}.
      </p>

      {withoutEconomics > 0 && (
        <p className={styles.notice} data-testid="stored-economics-gap">
          {withoutEconomics} order{withoutEconomics === 1 ? '' : 's'} in this population carry no
          stored economics row and are counted separately — never as $0.00, which would
          understate every figure below.
        </p>
      )}

      <div className={styles.kpiGrid} data-testid="stored-economics-grid">
        {STORED_DEFS.map((def) => (
          <StoredTile key={def.key} def={def} metric={stored?.[def.key]} />
        ))}
      </div>

      <p className={styles.scopeLine} data-testid="stored-economics-quality">
        Line quality — verified {quality.verified ?? 0} · estimated {quality.estimated ?? 0} ·
        missing {quality.missing ?? 0} · conflicting {quality.conflicting ?? 0}
        {Number(stored.unresolvedLines ?? 0) > 0
          ? ` · ${stored.unresolvedLines} unresolved line(s)`
          : ''}
      </p>
    </div>
  );
};

export default RevenueStoredEconomicsRow;
